// X.509 / CMS / OpenPGP / RSA signing-key tab body — extracted from
// the original AdminTrust page so it can co-exist with the Sigstore
// and OIDC trust tabs under one consolidated `/admin/trust` shell.

import { useState } from 'react';
import { Plus, Trash2, Download, AlertTriangle } from 'lucide-react';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import {
  useTrustKeys,
  createTrustKey,
  setTrustFlag,
  retireTrustKey,
  publicKeyURL,
  type TrustKey,
  type TrustAlgorithm,
  type TrustKeyCreateInput,
} from '../../api/trust';

const PURPOSE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'debian_release', label: 'Debian — Release / InRelease' },
  { value: 'rpm_repomd', label: 'RPM — repomd.xml' },
  { value: 'alpine_apkindex', label: 'Alpine — APKINDEX' },
  { value: 'opkg_packages', label: 'Opkg — Packages' },
  { value: 'swift_manifest', label: 'Swift — manifest CMS' },
  { value: 'hex_index', label: 'Hex — index' },
  { value: 'nix_narinfo', label: 'Nix — narinfo (usign)' },
  { value: 'p2_bundle', label: 'Eclipse p2 — bundle' },
  { value: 'maven_artifact', label: 'Maven — artifact .asc' },
  { value: 'jetbrains_plugin', label: 'JetBrains plugin — .sig CMS' },
  { value: 'vsix_extension', label: 'VSIX — embedded extension.signature.p7s' },
  { value: 'nuget_package', label: 'NuGet — embedded .signature.p7s' },
];

const ALGO_OPTIONS: Array<{ value: TrustAlgorithm; label: string; hint: string }> = [
  {
    value: 'ed25519',
    label: 'Ed25519',
    hint: 'compact 64-byte signatures, used by Nix / Hex',
  },
  {
    value: 'rsa-4096',
    label: 'RSA-4096',
    hint: 'PEM SubjectPublicKeyInfo, used by Alpine / Opkg',
  },
  {
    value: 'openpgp-rsa-4096',
    label: 'OpenPGP RSA-4096',
    hint: 'ASCII-armored, used by Debian / RPM / Maven',
  },
];

const SELECT_CLASS =
  'w-full px-3 py-2 rounded-md bg-bg-inset text-fg border border-border focus:border-accent focus:bg-bg-elevated transition-colors text-sm';
const LABEL_CLASS = 'text-sm text-fg-muted block mb-1.5';

function emptyDraft(): TrustKeyCreateInput {
  return {
    purpose: PURPOSE_OPTIONS[0].value,
    algorithm: 'openpgp-rsa-4096',
    repo_id: '',
    trusted_for_verify: true,
  };
}

function fingerprintPreview(fp: string): string {
  if (!fp) return '';
  if (fp.length <= 24) return fp;
  return `${fp.slice(0, 12)}…${fp.slice(-8)}`;
}

function purposeLabel(p: string): string {
  const m = PURPOSE_OPTIONS.find((o) => o.value === p);
  return m ? m.label : p;
}

function ActiveKeysBody({
  isLoading,
  keys,
  onToggleTrust,
  onRetire,
}: {
  isLoading: boolean;
  keys: TrustKey[];
  onToggleTrust: (k: TrustKey) => void;
  onRetire: (k: TrustKey) => void;
}) {
  if (isLoading) {
    return <p className="text-sm text-fg-muted">Loading…</p>;
  }
  if (keys.length === 0) {
    return (
      <p className="text-sm text-fg-muted">
        No active signing keys. Mint one above to start signing /
        verifying.
      </p>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-fg-muted border-b border-border">
            <th className="py-2 pr-4">Purpose</th>
            <th className="py-2 pr-4">Algorithm</th>
            <th className="py-2 pr-4">Scope</th>
            <th className="py-2 pr-4">Fingerprint</th>
            <th className="py-2 pr-4">Verify-trust</th>
            <th className="py-2 pr-4">Created</th>
            <th className="py-2 pr-4" />
          </tr>
        </thead>
        <tbody>
          {keys.map((k) => (
            <tr key={k.id} className="border-b border-border/50">
              <td className="py-2 pr-4 font-medium">
                {purposeLabel(k.purpose)}
              </td>
              <td className="py-2 pr-4 text-xs">
                <Badge tone="neutral">{k.algorithm}</Badge>
              </td>
              <td className="py-2 pr-4">
                {k.repo_id ? (
                  <Badge tone="accent">
                    repo {k.repo_id.slice(0, 8)}
                  </Badge>
                ) : (
                  <Badge tone="neutral">global</Badge>
                )}
              </td>
              <td className="py-2 pr-4 font-mono text-xs">
                {fingerprintPreview(k.fingerprint)}
              </td>
              <td className="py-2 pr-4">
                <button
                  className="text-xs underline"
                  onClick={() => onToggleTrust(k)}
                >
                  {k.trusted_for_verify ? 'trusted' : 'off'}
                </button>
              </td>
              <td className="py-2 pr-4 text-xs text-fg-muted">
                {new Date(k.created_at).toLocaleDateString()}
                <div className="text-fg-subtle">{k.created_by}</div>
              </td>
              <td className="py-2 pr-4">
                <div className="flex gap-1 justify-end">
                  <a
                    href={publicKeyURL(k.purpose, k.repo_id)}
                    aria-label="download public material"
                    title="Download public key"
                    className="inline-flex items-center justify-center h-8 px-3 rounded-md bg-transparent text-fg hover:bg-bg-hover transition-colors"
                  >
                    <Download className="h-3.5 w-3.5" />
                  </a>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onRetire(k)}
                    aria-label="retire"
                    title="Retire key"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function AdminTrustX509Tab() {
  const { data, error, mutate, isLoading } = useTrustKeys();
  const [draft, setDraft] = useState<TrustKeyCreateInput>(emptyDraft);
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const keys = data ?? [];
  const trustedCount = keys.filter((k) => k.trusted_for_verify).length;

  async function handleSubmit() {
    setBusy(true);
    setErrorMsg(null);
    setSuccessMsg(null);
    try {
      const payload: TrustKeyCreateInput = {
        purpose: draft.purpose.trim(),
        algorithm: draft.algorithm,
        trusted_for_verify: !!draft.trusted_for_verify,
      };
      if (draft.repo_id && draft.repo_id.trim() !== '') {
        payload.repo_id = draft.repo_id.trim();
      }
      const res = await createTrustKey(payload);
      await mutate();
      setDraft(emptyDraft());
      setSuccessMsg(
        `Minted ${res.algorithm} key for "${purposeLabel(res.purpose)}" (fingerprint ${fingerprintPreview(res.fingerprint)}).`,
      );
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function toggleTrust(k: TrustKey) {
    try {
      await setTrustFlag(k.id, !k.trusted_for_verify);
      await mutate();
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : String(e));
    }
  }

  async function handleRetire(k: TrustKey) {
    if (
      !confirm(
        `Retire signing key for "${purposeLabel(k.purpose)}" (${k.algorithm}, fingerprint ${fingerprintPreview(k.fingerprint)})?\n\nThe key will stop signing new artifacts and stop being trusted for verify. This is not reversible.`,
      )
    ) {
      return;
    }
    try {
      await retireTrustKey(k.id);
      await mutate();
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <>
      {error && (
        <Card className="mb-4 border-danger/40">
          <CardBody>
            <p className="text-sm text-danger">
              Failed to load signing keys: {String(error)}
            </p>
          </CardBody>
        </Card>
      )}

      {!isLoading && trustedCount === 0 && (
        <Card className="mb-4 border-warning/40 bg-warning/5">
          <CardBody>
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-warning shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-fg">
                  Verify-on-pull is disabled
                </p>
                <p className="text-sm text-fg-muted mt-1">
                  No signing key in this deployment is currently trusted for
                  verify. Adapters that gate pulls on signature verification
                  fall back to "accept unsigned". Mint a key below (or flip
                  the trust flag on an existing one) to start gating
                  upstream pulls on signature checks.
                </p>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      <Card className="mb-6">
        <CardHeader title="Mint a signing key" />
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="x509-purpose" className={LABEL_CLASS}>
                Purpose
              </label>
              <select
                id="x509-purpose"
                className={SELECT_CLASS}
                value={draft.purpose}
                onChange={(e) =>
                  setDraft({ ...draft, purpose: e.target.value })
                }
              >
                {PURPOSE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-fg-muted mt-1.5">
                Format-specific role string. The same purpose can hold one
                global key plus any number of per-repo overrides.
              </p>
            </div>
            <div>
              <label htmlFor="x509-algorithm" className={LABEL_CLASS}>
                Algorithm
              </label>
              <select
                id="x509-algorithm"
                className={SELECT_CLASS}
                value={draft.algorithm}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    algorithm: e.target.value as TrustAlgorithm,
                  })
                }
              >
                {ALGO_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-fg-muted mt-1.5">
                {ALGO_OPTIONS.find((o) => o.value === draft.algorithm)?.hint}
              </p>
            </div>
            <div className="md:col-span-2">
              <label htmlFor="x509-repo-id" className={LABEL_CLASS}>
                Repo ID (blank = global)
              </label>
              <Input
                id="x509-repo-id"
                value={draft.repo_id ?? ''}
                onChange={(e) =>
                  setDraft({ ...draft, repo_id: e.target.value })
                }
                placeholder="leave empty for whole-instance key"
              />
              <p className="text-xs text-fg-muted mt-1.5">
                Per-repo keys override the global key for that purpose. Use
                this for adapters whose upstream mirror runs its own keyring
                (e.g. an internal Maven mirror that signs with the org's
                build CA, distinct from Maven Central).
              </p>
            </div>
            <div className="md:col-span-2">
              <label className="flex items-center gap-2 text-sm text-fg-muted">
                <input
                  type="checkbox"
                  checked={!!draft.trusted_for_verify}
                  onChange={(e) =>
                    setDraft({ ...draft, trusted_for_verify: e.target.checked })
                  }
                />
                <span>
                  Trust this key for verify-on-pull{' '}
                  <span className="text-xs text-fg-subtle ml-2">
                    (the public half is anchored as a trust root for
                    upstream signature verification)
                  </span>
                </span>
              </label>
            </div>
          </div>
          {errorMsg && (
            <p className="text-sm text-danger mt-3">{errorMsg}</p>
          )}
          {successMsg && (
            <p className="text-sm text-emerald-500 mt-3">{successMsg}</p>
          )}
          <div className="flex gap-2 mt-4">
            <Button variant="primary" onClick={handleSubmit} disabled={busy}>
              <Plus className="h-4 w-4 mr-1" />
              {busy ? 'Minting…' : 'Mint key'}
            </Button>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title={`Active keys (${keys.length})`} />
        <CardBody>
          <ActiveKeysBody
            isLoading={isLoading}
            keys={keys}
            onToggleTrust={toggleTrust}
            onRetire={handleRetire}
          />
        </CardBody>
      </Card>
    </>
  );
}
